/**
 * The controls that narrow a list, in a panel that collapses.
 *
 * Two parallel implementations existed, one for the workspace and one for
 * reports, and neither offered a way back to the unfiltered list except
 * resetting each control in turn.
 *
 * Collapsed by default: on a list that fits a screen the panel is chrome, and
 * the summary carries the count and a "Filtered" badge, so a narrowed list
 * still says so with the panel shut.
 *
 * The filters take the panel's whole width, which is what lets a caller lay
 * them out in columns; the clear control sits on its own row beneath them,
 * against the right edge. The header row is one line while it fits (label, count),
 * and the count wraps beneath the label rather than shortening it. The
 * `trailing` controls (a view toggle, a sort) sit against the right edge, outside
 * the collapsing control so pressing them never opens the panel.
 *
 * @module components/ui/FilterBar
 */

import React, { type ReactNode, useId, useState } from "react";

import { Button } from "./Button";
import { EditLockExempt } from "./EditLock";
import { Icon } from "./Icon";

export interface FilterBarProps {
  children: ReactNode;
  /** Shown only when something is actually filtered. */
  onClear?: () => void;
  /** Whether any filter is currently narrowing the list. */
  active?: boolean;
  /** Names the region, e.g. "Filter programmes". */
  "aria-label": string;
  /** Heading on the control that collapses the panel. */
  summary?: string;
  /**
   * How much of the list is showing. Sits on the header row rather than among
   * the controls: inside the panel it is the one count on the screen and it
   * disappears with the panel, so a narrowed list reported nothing.
   *
   * A string, not a node: it is rendered twice, once for the eye and once for
   * the live region, and a node carrying a `data-testid` would put the same id
   * on two elements.
   */
  count?: string;
  /** Names the visible count, for a screen whose spec already clicks it. */
  countTestId?: string;
  /** Whether the panel starts open. */
  defaultOpen?: boolean;
  /** Controls at the right end of the header row, beside the collapse control. */
  trailing?: ReactNode;
  /**
   * `card` is a surface of its own above a list. `flush` drops the border,
   * fill and margin, for a bar that sits inside a surface that already has
   * them: the head of a master list.
   */
  variant?: "card" | "flush";
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <FilterBar aria-label="Filter programmes" active={isFiltered} onClear={reset}>
 *   <TextField … /><SelectField … />
 * </FilterBar>
 * ```
 */
export function FilterBar({
  children,
  onClear = undefined,
  active = false,
  "aria-label": label,
  summary = "Filters",
  count = undefined,
  countTestId = undefined,
  defaultOpen = false,
  trailing = undefined,
  variant = "card",
  className = "",
  "data-testid": testId,
}: FilterBarProps): React.ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const shell =
    variant === "card" ? "mb-5 rounded-box border border-base-300 bg-base-200" : "bg-base-100";
  return (
    <section aria-label={label} className={`${shell} ${className}`.trim()} data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-2">
        {/* A button, not a `summary`: the trailing controls share this row and
            a control inside a summary toggles the panel with it. */}
        <button
          type="button"
          className="flex min-h-9 min-w-0 flex-1 cursor-pointer flex-wrap items-center gap-x-2 text-left font-semibold"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            setOpen((current) => !current);
          }}
          data-testid="filter-panel-toggle"
        >
          {/* The funnel fills and takes the accent when something is set. At
              16px the fill barely reads, so the hue is doing the work and the
              badge beside it is what carries the state without colour. */}
          <Icon
            name="funnel"
            weight={active ? "fill" : "regular"}
            className={active ? "text-primary-strong" : ""}
            aria-hidden
          />
          <span>{summary}</span>
          {active && (
            <span
              className="shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium"
              data-testid="filter-active"
            >
              Filtered
            </span>
          )}
          {count != null && (
            <span className="shrink-0 text-xs font-normal text-muted" data-testid={countTestId}>
              {count}
            </span>
          )}
          <Icon
            name="caret-down"
            className={`ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
        {trailing}
      </div>
      {/* Filters choose what is shown and never write, so a lock leaves them be. */}
      <EditLockExempt>
        <div
          id={panelId}
          hidden={!open}
          className="border-t border-base-300 p-4"
          data-testid="filter-panel"
        >
          {children}
          {onClear && (
            <div className="mt-4 flex justify-end">
              {/* Always mounted and always enabled, dimmed when there is
                  nothing to clear: unmounting it drops the focus of the
                  keyboard user who just pressed it, and so does disabling it,
                  since the browser blurs a control the moment it becomes
                  disabled. */}
              <Button
                variant="ghost"
                size="sm"
                icon="x"
                onClick={active ? onClear : undefined}
                aria-disabled={!active || undefined}
                data-testid="filter-clear"
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </EditLockExempt>
      {/* The spoken copy of the count, outside the toggle. A button is
          name-from-content, so a live region inside it is folded into the
          control's own name and never announced as a region. */}
      {count != null && (
        <span role="status" className="sr-only">
          {count}
        </span>
      )}
    </section>
  );
}
